import React from 'react';
import {
  type MenuTemplateProps,
  ENTREE_CATS,
  MENU_DESCRIPTION_DEFAULT,
  CENTERED_MENU_DESCRIPTION_STYLE,
  MenuLogo,
  FitToPage,
  cleanItems,
  extractAlwaysAvailableItems,
  formatDisplayDate,
  groupForMealDisplay,
} from '../shared/menu-print-shared';

export function MealTemplate({
  communityName,
  date,
  showDate = true,
  bugImageUrl,
  sections,
  alwaysAvailable,
  textAlign = 'center',
}: MenuTemplateProps) {
  const menuDescription = MENU_DESCRIPTION_DEFAULT;
  const rawItems = sections.flatMap((s) => s.items);
  const allItems = cleanItems(rawItems);
  const menuSections = groupForMealDisplay(allItems);
  const alwaysItems = cleanItems(extractAlwaysAvailableItems(alwaysAvailable));

  const logo = (
    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
      {bugImageUrl && <MenuLogo bugImageUrl={bugImageUrl} />}
    </div>
  );

  const footer = (
    <div style={{ textAlign: 'center', marginTop: '8px' }}>
      <div style={{
        ...CENTERED_MENU_DESCRIPTION_STYLE,
        fontSize: '11px',
        color: '#333',
        lineHeight: '1.6',
        whiteSpace: 'pre-line',
      }}>
        {menuDescription}
      </div>
      {showDate && date ? (
        <div style={{
          fontSize: '10px',
          color: '#888',
          marginTop: '6px',
        }}>
          {formatDisplayDate(date)}
        </div>
      ) : null}
    </div>
  );

  return (
    <FitToPage fixedContent={logo} fixedFooter={footer} showBorder>
      <div style={{
        textAlign,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 'inherit',
      }}>
        {menuSections.map((section) => (
          <div key={section.title}>
            <div style={{
              marginTop: '28px',
              marginBottom: '12px',
              textAlign: 'center',
              fontSize: '34px',
              fontWeight: 'normal',
              fontFamily: '"Times New Roman", Times, serif',
            }}>
              {section.title}
            </div>
            {section.items.map((item, idx) => {
              const hasDescription = !!item.RecipeDescription?.trim();
              const isEntree = ENTREE_CATS.has(item.MenuCategory?.trim() ?? '');
              return (
                <div key={idx} style={{ marginBottom: '14px' }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: isEntree ? 'bold' : 'normal',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    whiteSpace: 'pre-line',
                    lineHeight: '1.2',
                  }}>
                    {item.RecipeName}
                  </div>
                  {hasDescription ? (
                    <div style={{
                      ...CENTERED_MENU_DESCRIPTION_STYLE,
                      fontStyle: 'italic',
                      fontSize: '14px',
                      color: '#333',
                      lineHeight: '1.2',
                      marginTop: '1px',
                      whiteSpace: 'pre-line',
                    }}>
                      {item.RecipeDescription}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}

        {/* Always available */}
        {alwaysItems.length > 0 && (
          <div style={{
            marginTop: '24px',
            textAlign: 'center',
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '12px',
              color: '#555',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '3px',
            }}>
              Always Available
            </div>
            <div style={{
              fontStyle: 'italic',
              fontSize: '12px',
              color: '#555',
              lineHeight: '1.5',
            }}>
              {alwaysItems.map((i) => i.RecipeName).join('  \u2022  ')}
            </div>
          </div>
        )}
      </div>
    </FitToPage>
  );
}
